import {Injectable} from "@angular/core"
import {Subject} from "rxjs"
import {IsDefined} from "@cm/utils"

export type ConnectionType<PointType = unknown> = {id: string; source: PointType; destination: PointType}

@Injectable({
    providedIn: "root",
})
export class ConnectionsService<Connection extends ConnectionType> {
    connections: Connection[] = []

    readonly onAdd$ = new Subject<Connection[]>()
    readonly onUpdate$ = new Subject<Connection[]>()
    readonly onDelete$ = new Subject<Connection[]>()

    add = (connections: Connection | Connection[]) => {
        if (!Array.isArray(connections)) {
            connections = [connections]
        }
        const newConnections = connections.filter(
            (connection: Connection) => !this.connections.some((existingConnection) => existingConnection.id === connection.id),
        )
        this.connections.push(...newConnections)
        this.onAdd$.next(newConnections)
    }

    remove = (connectionIdsToBeDeleted: string | string[]) => {
        if (!Array.isArray(connectionIdsToBeDeleted)) {
            connectionIdsToBeDeleted = [connectionIdsToBeDeleted]
        }
        for (const connectionIdToBeDeleted of connectionIdsToBeDeleted) {
            const groupedConnections = Object.groupBy(this.connections, (connection) => (connection.id === connectionIdToBeDeleted ? "deleted" : "remaining"))

            this.connections = groupedConnections.remaining ?? []

            if (groupedConnections.deleted?.length) {
                this.onDelete$.next(groupedConnections.deleted)
            }
        }
    }

    removeAll = () => {
        const allConnections = this.connections
        this.connections = []
        this.onDelete$.next(allConnections)
    }

    update = (updatedConnections: Connection | Connection[]) => {
        if (!Array.isArray(updatedConnections)) {
            updatedConnections = [updatedConnections]
        }
        this.connections = this.connections.map((connection) => {
            const update = updatedConnections.find((updatedConnection) => updatedConnection.id === connection.id)
            return update ?? connection
        })
        this.onUpdate$.next(
            this.connections.filter((connection) => updatedConnections.find((updatedConnection) => updatedConnection.id === connection.id)).filter(IsDefined),
        )
    }

    updateAll = () => {
        this.onUpdate$.next(this.connections)
    }

    synchronize = (connections: Connection[]) => {
        const {updatedConnections, deletedConnections} = Object.groupBy(this.connections, (existingConnection) =>
            connections.some((newConnection) => newConnection.id === existingConnection.id) ? "updatedConnections" : "deletedConnections",
        )
        this.add(connections)
        this.remove(deletedConnections?.map((connection) => connection.id) ?? [])
        this.onUpdate$.next(updatedConnections ?? [])
    }

    get = (connectionId: string) => {
        return this.connections.find((connection) => connection.id === connectionId)
    }
}
